import { useState } from "react";
import { supabase } from "../lib/supabase";
import {
  VISIBLE_RANGES, narrowsFromToday, rangeConsequence, rangeTile, type VisibleRange,
} from "../lib/visibleRange";

/* W16 — HOW FAR AHEAD THIS CLIENT MAY LOOK. One column on the relationship's own row,
 * `coach_clients.visible_range`, set by the coach and read by the ATHLETE'S PHONE.
 *
 * 🔴 **A SEGMENTED ROW OF TILES — THEIRS, COPIED** (`docs/EVERFIT-MAP.md` §2-ter, read live on his
 * account 2026-09-19; his copy order of ~08:55, Fable's ruling ~10:00). Everfit's
 * `Date range visible to client` is four tiles in one row with the chosen one outlined and
 * lettered in blue; ours is the same row with OUR accent and a fifth tile.
 *
 * ⚠️ **THIS REPLACES FIVE FULL-WIDTH ROWS OF RADIOS THAT WERE RENDERED AND PASSED FOUR HOURS
 * EARLIER**, and the reason is the copy rule rather than a fault in them: under §7.2 the judge of
 * every web surface during the copy phase is Everfit's own screen, not taste, and a shape that
 * departs from theirs is not kept merely because it was built first. The argument the rows were
 * built on — *"the consequence is the thing a coach is actually choosing between"* — is not
 * thrown away: it moves to the SUBTITLE, where the chosen tile's consequence is printed once,
 * live, under the row. Five sentences at rest became one that answers the tile you are on.
 *
 * ⚠️ **THE FIFTH TILE IS OURS AND STAYS** (Fable, explicitly). Four values cannot express "any
 * week you have assigned", which is what every athlete on this database can reach today; matching
 * their COUNT would narrow a live behaviour to copy a screen. `'all'` also remains the
 * behaviour-preserving resolution of anything unrecognised — see `lib/visibleRange.ts`.
 *
 * ⚠️ **RADIOS UNDERNEATH, NOT BUTTONS.** A segmented control that chooses one of five IS a radio
 * group; `<button>`s would look identical and lose arrow-key traversal, the group's accessible
 * name and the "one of these is current" fact that a screen reader states. The input is visually
 * hidden INSIDE its tile, never `display: none` — a hidden-by-display input is unfocusable, which
 * would take the keyboard path away while looking exactly the same in a still.
 *
 * ⚠️ **THE GUARD IS THE DATABASE'S, NOT THIS COMPONENT'S** — the same argument `AutoApplySwitch`
 * makes. The CHECK refuses a sixth value and the update policy admits only the coach's own rows;
 * this sends the change and shows the server's own sentence if it is refused. A second copy of
 * that rule here is the one that would drift.
 *
 * 🔴 **AND IT SAYS WHAT IT IS NOT.** This gates BROWSING, never access: the athlete's data is
 * exactly as readable as it was and nothing is hidden from them. A coach who believed they had
 * hidden something would be wrong in a way that matters, so the screen says it rather than a
 * comment nobody opens.
 */
export function VisibleRangeControl({ linkId, initial, disabled }: {
  /** `coach_clients.id` — the relationship, not the client. */
  linkId: string;
  /** The stored value, or **undefined when the column is not in the row at all** — see below. */
  initial: VisibleRange | undefined;
  /** A relationship that is not active has nothing to configure. */
  disabled: boolean;
}) {
  const [value, setValue] = useState<VisibleRange | undefined>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* 🔴 THE COLUMN IS LIVE SINCE 2026-09-19 09:10, SO THIS BRANCH CHANGED ITS MEANING AND ITS
   * SENTENCE WITH IT. It used to say the column was missing, which is now FALSE — and a sentence
   * that quietly stops being true is this repo's most expensive habit. `undefined` today means
   * THE READ DID NOT COME BACK: a refused SELECT, a dropped request, a row that is not there.
   * ⚠️ THE BRANCH STAYS. Rendering the tiles anyway would offer a coach a choice that may not
   * save, and defaulting one would show them a setting their client is not actually under — a
   * fallback nobody can see is a default. */
  if (value === undefined) {
    return (
      <p className="ht-caption client-settings-cost">
        Could not read this setting. Reload the page to try again.
      </p>
    );
  }

  async function choose(next: VisibleRange) {
    setBusy(true);
    setError(null);
    /* ⚠️ TYPED, SINCE 2026-09-19. This was an untyped cast behind an eslint-disable while the
       column was not in `database.types.ts`; the migration landed at 09:10 and the types were
       regenerated, so the cast is GONE rather than left "for safety". A cast that outlives its
       reason is what makes the next wrong column name compile. */
    const { error: err } = await supabase
      .from("coach_clients")
      .update({ visible_range: next })
      .eq("id", linkId);
    setBusy(false);
    if (err !== null) {
      // The server's own sentence. A 42501 here means the column is not writable by this caller,
      // and paraphrasing it would hide which of several reasons applied.
      setError(err.message);
      return;
    }
    setValue(next);
  }

  return (
    <div className="visible-range">
      {/* ⚠️ THE LEGEND IS THE FIELDSET'S ACCESSIBLE NAME AND IS VISUALLY HIDDEN, NOT DELETED. The
          panel already prints `Weeks they can look at` as an `<h3>` one line above, so a visible
          legend renders the same six words twice — which the 2026-09-19 render showed. Deleting
          it instead would leave a group of five radios with no name for a screen reader: an
          `<h3>` sitting above a `<fieldset>` is not programmatically its label. */}
      <fieldset className="visible-range-set" disabled={disabled || busy}>
        <legend className="ht-visually-hidden">Weeks they can look at</legend>
        <div className="visible-range-tiles">
          {VISIBLE_RANGES.map((v) => (
            /* 🔴 ONE TILE PER VALUE, THE CHOSEN ONE OUTLINED IN THE ACCENT — theirs, in our
               tokens. `aria-hidden` is NOT used on the input: it is the control. */
            <label className={value === v ? "visible-range-tile is-on" : "visible-range-tile"}
                   key={v}>
              <input
                type="radio"
                name={`visible-range-${linkId}`}
                value={v}
                checked={value === v}
                onChange={() => { void choose(v); }}
              />
              <span className="visible-range-tile-name">{rangeTile(v)}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {/* 🔴 THE SUBTITLE — ONE SENTENCE, UNDER THE ROW, ABOUT THE TILE THAT IS ON. Theirs prints a
          fixed instruction here (*"Choose range you would like your client to have access to…"*),
          which tells a coach what the control is rather than what their own choice does. Ours
          prints the chosen value's consequence, which is the half the five rows existed for and
          the only thing on this screen that changes when a tile is pressed. */}
      <p className="ht-caption visible-range-why" aria-live="polite">{rangeConsequence(value)}</p>
      {/* ⚠️ ONE MORE SENTENCE, SHOWN ONLY WHEN IT APPLIES — and it is OURS, recorded as a
          departure (§7.2) rather than slipped in. Every value except "Any week" takes something
          away from what the athlete can reach today, and a coach should learn that here rather
          than from an athlete's message. It is not a defect in theirs that we are fixing; it is a
          claim about OUR database that would be untrue by omission. */}
      {narrowsFromToday(value) && (
        <p className="ht-caption visible-range-note">
          This is narrower than what they can reach today. It changes what they can BROWSE — their
          own history and results are untouched, and nothing is hidden from them.
        </p>
      )}
      {error !== null && <p className="ht-callout identity-error" role="alert">{error}</p>}
    </div>
  );
}
